import { useCallback, useMemo } from 'react'

import { useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_BOOKING_OUTCOMES } from 'src/graphql/callAnaltyics.graphql'
import {
  LeaderboardCardView,
  type LeaderboardColumn,
} from 'src/pages/AnalyticsPage/components/LeaderboardCardView'
import { StackedBar } from 'src/pages/AnalyticsPage/components/StackedBar'

import type { BookingOutcomeFiltersInput } from 'types/graphql'

import { BOOKING_OUTCOME_COLORS } from './bookingOutcomeChartTheme'

export const BOOKINGS_BY_RECRUITER_INFO_TEXT =
  'Bookings grouped by the assigned recruiter.'

interface BookingOutcomeRow {
  id: string
  recruiterId: string
  recruiterName: string
  accepted: number
  noShow: number
  cancelled: number
  total: number
}

const renderCount = (n: number) =>
  n === 0 ? <span className="text-gray-400">0</span> : n.toLocaleString()

const renderOutcomeSplit = (row: BookingOutcomeRow) => (
  <StackedBar
    segments={[
      {
        id: 'completed',
        value: row.accepted,
        color: BOOKING_OUTCOME_COLORS.completed,
        label: `Completed · ${row.accepted}`,
      },
      {
        id: 'no-show',
        value: row.noShow,
        color: BOOKING_OUTCOME_COLORS.noShow,
        label: `No-show · ${row.noShow}`,
      },
      {
        id: 'cancelled',
        value: row.cancelled,
        color: BOOKING_OUTCOME_COLORS.cancelled,
        label: `Cancelled · ${row.cancelled}`,
      },
    ]}
    height={8}
  />
)

export const BookingOutcomesByEmployeeTable = ({
  filters,
}: {
  filters: BookingOutcomeFiltersInput
}) => {
  const onError = useCallback(() => {
    toast('Failed to load bookings by recruiter', 'error')
  }, [])

  const { data, loading } = useQuery(GET_BOOKING_OUTCOMES, {
    variables: { filters, groupByRecruiter: true },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const rows = useMemo<ReadonlyArray<BookingOutcomeRow>>(() => {
    const items = data?.getBookingOutcomes ?? []
    return items.map((row) => ({
      id: row.recruiterId,
      recruiterId: row.recruiterId,
      recruiterName: row.recruiterName,
      accepted: row.accepted,
      noShow: row.noShow,
      cancelled: row.cancelled,
      total: row.total,
    }))
  }, [data])

  const columns: ReadonlyArray<LeaderboardColumn<BookingOutcomeRow>> = [
    {
      key: 'recruiterName',
      label: 'Recruiter',
      align: 'left',
      sortable: true,
    },
    {
      key: 'accepted',
      label: 'Completed',
      render: (row) => renderCount(row.accepted),
    },
    {
      key: 'noShow',
      label: 'No-show',
      render: (row) => renderCount(row.noShow),
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      render: (row) => renderCount(row.cancelled),
    },
    {
      key: 'total',
      label: 'Total',
      render: (row) => (
        <span className="font-medium">{renderCount(row.total)}</span>
      ),
    },
    {
      key: 'recruiterId',
      label: 'Outcomes',
      align: 'left',
      width: 140,
      sortable: false,
      render: renderOutcomeSplit,
    },
  ]

  const totals = useMemo(() => {
    if (rows.length === 0) return undefined

    const summed = rows.reduce(
      (acc, row) => ({
        accepted: acc.accepted + row.accepted,
        noShow: acc.noShow + row.noShow,
        cancelled: acc.cancelled + row.cancelled,
        total: acc.total + row.total,
      }),
      { accepted: 0, noShow: 0, cancelled: 0, total: 0 }
    )

    return {
      accepted: summed.accepted.toLocaleString(),
      noShow: summed.noShow.toLocaleString(),
      cancelled: summed.cancelled.toLocaleString(),
      total: summed.total.toLocaleString(),
    }
  }, [rows])

  return (
    <LeaderboardCardView<BookingOutcomeRow>
      columns={columns}
      rows={rows}
      loading={loading}
      emptyMessage="No bookings in this period"
      defaultSortKey="total"
      defaultSortDir="desc"
      totals={totals}
    />
  )
}

export default BookingOutcomesByEmployeeTable
